/**
 * Сохранить сообщение как заготовку.
 *
 * Единственный путь получить в заготовку премиальный эмодзи: у него нет
 * символа, только сущность разметки, и взять его можно лишь из сообщения, где
 * он уже есть. Заодно так сохраняется и вложение — фото или видео уходят в
 * заготовку вместе с текстом.
 */

import { useEffect, useState } from "react";
import type { TelegramMessageDto } from "@tw/shared-types";
import { useSession } from "../state/session.tsx";
import { describeError } from "../lib/errors.js";
import { telegramIdOf } from "../lib/message-id.js";
import type { QuickReplyFolderDto } from "../lib/api.js";
import { RichText } from "./RichText.tsx";
import { FIELD } from "./controls.js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface SaveAsTemplateDialogProps {
  chatId: string;
  message: TelegramMessageDto | null;
  onClose: () => void;
}

export function SaveAsTemplateDialog({ chatId, message, onClose }: SaveAsTemplateDialogProps) {
  const { api } = useSession();
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState("");
  const [folders, setFolders] = useState<QuickReplyFolderDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!message) return;
    setTitle("");
    setFolderId("");
    setError(null);
    setDone(false);
    api
      .quickReplyFolders()
      .then(({ items }) => setFolders(items))
      .catch(() => setFolders([]));
  }, [api, message]);

  const media = message?.media;
  const mediaKind = media?.kind === "photo" || media?.kind === "video" ? media.kind : null;

  return (
    <Dialog open={message !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent key={message?.id ?? "none"}>
        <DialogHeader>
          <DialogTitle>В заготовки</DialogTitle>
          <DialogDescription>
            Текст сохранится с разметкой и премиальными эмодзи
            {mediaKind ? `, ${mediaKind === "photo" ? "фото" : "видео"} — вместе с ним` : ""}.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {message && (
            <div className="rounded-xl border border-border bg-secondary/50 px-3 py-2 text-[13px] leading-relaxed">
              {message.text ? (
                <p className="line-clamp-6 whitespace-pre-wrap">
                  <RichText text={message.text} entities={message.entities} chatId={chatId} />
                </p>
              ) : (
                <p className="text-muted-foreground">Без текста</p>
              )}
              {mediaKind && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  + {mediaKind === "photo" ? "фотография" : "видео"}
                </p>
              )}
            </div>
          )}

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Название, например «Прайс»"
            aria-label="Название заготовки"
            maxLength={80}
            autoFocus
            className={FIELD}
          />

          <div>
            <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Папка
            </p>
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              aria-label="Папка заготовки"
              className={cn(FIELD, "w-full")}
            >
              <option value="">Общая — видят все</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
          {done && <p className="text-xs text-[var(--ok)]">Заготовка сохранена</p>}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {done ? "Закрыть" : "Отмена"}
          </Button>
          {!done && (
            <Button
              size="sm"
              disabled={busy || title.trim().length === 0 || !message}
              onClick={() => {
                if (!message) return;
                setBusy(true);
                setError(null);
                void api
                  .createQuickReplyFromMessage({
                    chatId,
                    telegramMessageId: telegramIdOf(message) ?? message.id,
                    title: title.trim(),
                    folderId: folderId || null,
                  })
                  .then(() => setDone(true))
                  .catch((cause: unknown) => setError(describeError(cause)))
                  .finally(() => setBusy(false));
              }}
            >
              Сохранить
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
